import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCupomCategoriasControllerListar } from '@/api/generated/admin-cupom-categorias/admin-cupom-categorias';
import {
  getCuponsControllerListarQueryKey,
  getCuponsControllerObterQueryKey,
  useCuponsControllerAtualizar,
  useCuponsControllerCriar,
  useCuponsControllerEnviarImagem,
  useCuponsControllerObter,
} from '@/api/generated/admin-cupons/admin-cupons';
import type { CupomResponse } from '@/api/generated/model';
import { aplicarErrosDoServidor } from '@/api/erros';
import { ApiError } from '@/api/http-client';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { CampoDeImagem } from '@/shared/components/CampoDeImagem';
import { Alert, AlertDescription } from '@/shared/ui/alert';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select';
import { toast } from '@/shared/ui/sonner';
import { Switch } from '@/shared/ui/switch';
import { Textarea } from '@/shared/ui/textarea';

const SEM_CATEGORIA = 'nenhuma';

function ehUrlValida(valor: string): boolean {
  try {
    const url = new URL(valor);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

// --- Faixa --------------------------------------------------------------

const faixaSchema = z
  .object({
    ranqueMinimo: z.coerce.number().int().min(0).max(100),
    // `temCodigo` é só do formulário, igual o resto do módulo — o backend só
    // recebe `codigo` OU `linkRedirecionamento`.
    temCodigo: z.boolean(),
    codigo: z.string().trim().max(60),
    linkRedirecionamento: z.string().trim().max(500),
    // Texto, não número: campo em branco tem que virar `null`, não `0`.
    percentualDesconto: z.string().trim(),
  })
  .superRefine((valores, ctx) => {
    if (valores.temCodigo) {
      if (!valores.codigo) {
        ctx.addIssue({ code: 'custom', path: ['codigo'], message: 'Informe o código.' });
      }
    } else if (!valores.linkRedirecionamento) {
      ctx.addIssue({
        code: 'custom',
        path: ['linkRedirecionamento'],
        message: 'Informe o link de redirecionamento.',
      });
    } else if (!ehUrlValida(valores.linkRedirecionamento)) {
      ctx.addIssue({
        code: 'custom',
        path: ['linkRedirecionamento'],
        message: 'Informe uma URL começando com http:// ou https://.',
      });
    }

    if (valores.percentualDesconto) {
      const numero = Number(valores.percentualDesconto);
      if (!Number.isInteger(numero) || numero < 1 || numero > 100) {
        ctx.addIssue({
          code: 'custom',
          path: ['percentualDesconto'],
          message: 'Informe um número de 1 a 100, ou deixe em branco.',
        });
      }
    }
  });

type FormularioDeFaixa = z.input<typeof faixaSchema>;

const FAIXA_NOVA: FormularioDeFaixa = {
  ranqueMinimo: 0,
  temCodigo: true,
  codigo: '',
  linkRedirecionamento: '',
  percentualDesconto: '',
};

// --- Cupom ----------------------------------------------------------------

const schema = z
  .object({
    titulo: z.string().trim().min(3, 'Informe um título').max(200),
    descricao: z.string().trim().max(2000),
    regras: z.string().trim().max(4000),
    validoDe: z.string(),
    validoAte: z.string(),
    categoriaId: z.string(),
    faixas: z.array(faixaSchema).min(1, 'Adicione ao menos uma faixa de desconto.'),
  })
  .superRefine((valores, ctx) => {
    if (valores.validoDe && valores.validoAte && valores.validoDe > valores.validoAte) {
      ctx.addIssue({
        code: 'custom',
        path: ['validoAte'],
        message: 'O fim da vigência não pode ser antes do início.',
      });
    }

    const ranques = valores.faixas.map((f) => f.ranqueMinimo);
    if (new Set(ranques).size !== ranques.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['faixas'],
        message: 'Duas faixas não podem ter o mesmo ranque mínimo.',
      });
    }

    const codigos = valores.faixas.filter((f) => f.temCodigo).map((f) => f.codigo);
    if (new Set(codigos).size !== codigos.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['faixas'],
        message: 'Duas faixas não podem usar o mesmo código.',
      });
    }
  });

type Formulario = z.input<typeof schema>;

/** `<input type="date">` fala "2026-07-29"; a API fala ISO completo. */
function paraData(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

function paraIso(data: string, fimDoDia = false): string | null {
  if (!data) return null;
  return new Date(`${data}T${fimDoDia ? '23:59:59' : '00:00:00'}.000Z`).toISOString();
}

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  /** Ausente = criação. */
  cupom?: CupomResponse;
}

/**
 * ⚠️ `regras` não vem na listagem — só em `GET /admin/cupons/:id`.
 *
 * Por isso o diálogo de edição BUSCA o detalhe ao abrir, em vez de aproveitar o
 * objeto da linha. Sem esse fetch o campo abriria vazio e o salvamento apagaria
 * as regras em silêncio, que é exatamente o acidente que o campo separado cria.
 *
 * `faixas`, ao contrário de `regras`, já vem completo em QUALQUER resposta —
 * listagem incluída — mas o `reset()` ainda espera o `detalhe` no modo edição,
 * pra preencher os dois de uma vez só e não haver dois renders com formulário
 * mudando de baixo do admin.
 *
 * ⚠️ Salvar SEMPRE manda o array de `faixas` inteiro, mesmo editando: no
 * backend, o campo é substituição total quando presente, e este formulário
 * nunca omite — é a única forma de deixar de existir uma faixa que o admin
 * removeu na tela.
 */
export function CupomFormDialogo({ aberto, aoFechar, cupom }: Props) {
  const queryClient = useQueryClient();
  const editando = Boolean(cupom);

  const criar = useCuponsControllerCriar();
  const atualizar = useCuponsControllerAtualizar();
  const enviarImagem = useCuponsControllerEnviarImagem();

  const { data: detalhe, isPending: carregandoDetalhe } = useCuponsControllerObter(cupom?.id ?? '', {
    query: { enabled: aberto && Boolean(cupom) },
  });

  const { data: categorias } = useCupomCategoriasControllerListar({
    query: { enabled: aberto },
  });

  const {
    register,
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<Formulario>({ resolver: zodResolver(schema) });

  const { fields: faixas, append, remove } = useFieldArray({ control, name: 'faixas' });

  useEffect(() => {
    if (!aberto) return;
    // Na edição, espera o detalhe: é ele que traz `regras`.
    if (cupom && !detalhe) return;

    reset({
      titulo: detalhe?.titulo ?? '',
      descricao: detalhe?.descricao ?? '',
      regras: detalhe?.regras ?? '',
      validoDe: paraData(detalhe?.validoDe ?? null),
      validoAte: paraData(detalhe?.validoAte ?? null),
      categoriaId: detalhe?.categoria?.id ?? SEM_CATEGORIA,
      faixas: detalhe?.faixas.length
        ? detalhe.faixas.map((f) => ({
            ranqueMinimo: f.ranqueMinimo,
            temCodigo: Boolean(f.codigo),
            codigo: f.codigo ?? '',
            linkRedirecionamento: f.linkRedirecionamento ?? '',
            percentualDesconto: f.percentualDesconto?.toString() ?? '',
          }))
        : [FAIXA_NOVA],
    });
  }, [aberto, cupom, detalhe, reset]);

  const aoEnviar = handleSubmit(async (bruto) => {
    const dados = schema.parse(bruto);

    const corpo = {
      titulo: dados.titulo,
      descricao: dados.descricao.trim() || null,
      regras: dados.regras.trim() || null,
      validoDe: paraIso(dados.validoDe),
      validoAte: paraIso(dados.validoAte, true),
      categoriaId: dados.categoriaId === SEM_CATEGORIA ? null : dados.categoriaId,
      faixas: dados.faixas.map((f) => ({
        ranqueMinimo: f.ranqueMinimo,
        codigo: f.temCodigo ? f.codigo : null,
        linkRedirecionamento: f.temCodigo ? null : f.linkRedirecionamento,
        percentualDesconto: f.percentualDesconto ? Number(f.percentualDesconto) : null,
      })),
    };

    try {
      if (cupom) {
        await atualizar.mutateAsync({ id: cupom.id, data: corpo });
        await queryClient.invalidateQueries({
          queryKey: getCuponsControllerObterQueryKey(cupom.id),
        });
      } else {
        await criar.mutateAsync({ data: corpo });
      }

      await queryClient.invalidateQueries({ queryKey: getCuponsControllerListarQueryKey() });
      toast.success(editando ? 'Cupom atualizado.' : 'Cupom criado.');
      aoFechar();
    } catch (erro) {
      aplicarErrosDoServidor(erro, setError, 'Não foi possível salvar o cupom.');
    }
  });

  const carregando = editando && carregandoDetalhe;

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{editando ? 'Editar cupom' : 'Novo cupom'}</DialogTitle>
          <DialogDescription>
            Cada faixa vale a partir de um ranque mínimo — quem já alcançou mais de uma vê a mais
            vantajosa.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={aoEnviar} className="space-y-4" noValidate>
          {errors.root?.message && (
            <Alert variant="destructive">
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}

          <Campo id="titulo" rotulo="Título" erro={errors.titulo?.message} obrigatorio>
            <Input
              id="titulo"
              className={CLASSE_CONTROLE}
              disabled={carregando}
              {...register('titulo')}
            />
          </Campo>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-brand font-medium">
                Faixas de desconto
                <span className="text-destructive ml-0.5" aria-hidden="true">
                  *
                </span>
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={carregando}
                onClick={() => append(FAIXA_NOVA)}
              >
                <Plus className="w-4 h-4" />
                Adicionar faixa
              </Button>
            </div>

            {errors.faixas?.message && (
              <p className="text-xs text-destructive">{errors.faixas.message}</p>
            )}
            {errors.faixas?.root?.message && (
              <p className="text-xs text-destructive">{errors.faixas.root.message}</p>
            )}

            {faixas.map((faixa, indice) => (
              <FaixaDoFormulario
                key={faixa.id}
                indice={indice}
                idBase={faixa.id}
                register={register}
                control={control}
                erros={errors.faixas?.[indice]}
                carregando={Boolean(carregando)}
                aoRemover={faixas.length > 1 ? () => remove(indice) : undefined}
              />
            ))}
          </div>

          <Campo id="categoriaId" rotulo="Categoria" erro={errors.categoriaId?.message}>
            <Controller
              control={control}
              name="categoriaId"
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange} disabled={carregando}>
                  <SelectTrigger id="categoriaId" className={CLASSE_CONTROLE}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SEM_CATEGORIA}>Sem categoria</SelectItem>
                    {(categorias?.itens ?? [])
                      .filter((c) => c.ativo)
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.nome}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            />
          </Campo>

          <div className="grid grid-cols-2 gap-3">
            <Campo id="validoDe" rotulo="Válido de" erro={errors.validoDe?.message}>
              <Input
                id="validoDe"
                type="date"
                className={CLASSE_CONTROLE}
                disabled={carregando}
                {...register('validoDe')}
              />
            </Campo>

            <Campo
              id="validoAte"
              rotulo="Válido até"
              erro={errors.validoAte?.message}
              dica="Em branco = sem prazo."
            >
              <Input
                id="validoAte"
                type="date"
                className={CLASSE_CONTROLE}
                disabled={carregando}
                {...register('validoAte')}
              />
            </Campo>
          </div>

          <Campo
            id="descricao"
            rotulo="Descrição"
            erro={errors.descricao?.message}
            dica="Aparece para o médico na vitrine."
          >
            <Textarea
              id="descricao"
              rows={3}
              className={CLASSE_CONTROLE}
              disabled={carregando}
              {...register('descricao')}
            />
          </Campo>

          <Campo
            id="regras"
            rotulo="Regras internas"
            erro={errors.regras?.message}
            dica="Uso interno: nunca sai na vitrine nem em listagem da API."
          >
            <Textarea
              id="regras"
              rows={3}
              className={CLASSE_CONTROLE}
              disabled={carregando}
              {...register('regras')}
            />
          </Campo>

          {/* ⚠️ Só na edição, e não é limitação de layout: o upload é
              `POST /admin/cupons/:id/imagem`, e o `:id` só existe depois de
              criado. Oferecer o campo na criação daria um botão que não teria
              para onde enviar. */}
          {cupom && (
            <CampoDeImagem
              rotulo="Imagem do cupom"
              ajuda="PNG ou JPG em qualquer proporção — abre inteira num quadro 3:1 e você ajusta. Até 2 MB."
              urlAtual={detalhe?.imagemUrl ?? null}
              proporcao="aspect-[3/1]"
              recorte={{ proporcao: 3, larguraSaida: 1200, encaixar: true }}
              enviando={enviarImagem.isPending}
              aoEnviar={(arquivo) =>
                enviarImagem.mutate(
                  { id: cupom.id, data: { arquivo } },
                  {
                    onSuccess: () => {
                      void queryClient.invalidateQueries({
                        queryKey: getCuponsControllerObterQueryKey(cupom.id),
                      });
                      void queryClient.invalidateQueries({
                        queryKey: getCuponsControllerListarQueryKey(),
                      });
                      toast.success('Imagem atualizada.');
                    },
                    // A mensagem do servidor já cita a regra e o que foi
                    // enviado ("A imagem tem 800×400 (2.00:1). Esta posição
                    // exige 1:1…"). Reescrevê-la aqui criaria dois textos para
                    // divergir.
                    onError: (erro: unknown) =>
                      toast.error(
                        erro instanceof ApiError ? erro.message : 'Não foi possível enviar a imagem',
                      ),
                  },
                )
              }
            />
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={aoFechar} disabled={isSubmitting}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting || carregando}>
              {(isSubmitting || carregando) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editando ? 'Salvar' : 'Criar cupom'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Uma linha do array de faixas. `idBase` vem de `field.id` do
 * `useFieldArray` — estável por linha mesmo quando outra é removida no meio
 * (índice muda, `field.id` não), o que evita rótulo/campo desassociarem
 * silenciosamente numa remoção no meio da lista.
 */
function FaixaDoFormulario({
  indice,
  idBase,
  register,
  control,
  erros,
  carregando,
  aoRemover,
}: {
  indice: number;
  idBase: string;
  register: ReturnType<typeof useForm<Formulario>>['register'];
  control: ReturnType<typeof useForm<Formulario>>['control'];
  erros?: {
    ranqueMinimo?: { message?: string };
    codigo?: { message?: string };
    linkRedirecionamento?: { message?: string };
    percentualDesconto?: { message?: string };
  };
  carregando: boolean;
  aoRemover?: () => void;
}) {
  const idRanque = `faixa-${idBase}-ranque`;
  const idCodigo = `faixa-${idBase}-codigo`;
  const idLink = `faixa-${idBase}-link`;
  const idPercentual = `faixa-${idBase}-percentual`;
  const idTemCodigo = `faixa-${idBase}-tem-codigo`;

  return (
    <div className="rounded-lg border border-border p-3 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Label htmlFor={idRanque} className="text-xs text-muted-foreground whitespace-nowrap">
            Ranque mínimo
          </Label>
          <Input
            id={idRanque}
            type="number"
            min={0}
            className={CLASSE_CONTROLE + ' h-8 w-20'}
            disabled={carregando}
            {...register(`faixas.${indice}.ranqueMinimo`)}
          />

          <Label htmlFor={idPercentual} className="text-xs text-muted-foreground whitespace-nowrap ml-2">
            % desconto
          </Label>
          <Input
            id={idPercentual}
            type="number"
            min={1}
            max={100}
            placeholder="—"
            className={CLASSE_CONTROLE + ' h-8 w-16'}
            disabled={carregando}
            {...register(`faixas.${indice}.percentualDesconto`)}
          />
        </div>

        {aoRemover && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive shrink-0"
            aria-label="Remover faixa"
            disabled={carregando}
            onClick={aoRemover}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>
      {erros?.ranqueMinimo?.message && (
        <p className="text-xs text-destructive">{erros.ranqueMinimo.message}</p>
      )}
      {erros?.percentualDesconto?.message && (
        <p className="text-xs text-destructive">{erros.percentualDesconto.message}</p>
      )}

      <div className="flex items-center justify-between">
        <Label htmlFor={idTemCodigo} className="text-sm cursor-pointer">
          Esta faixa tem código
        </Label>
        <Controller
          control={control}
          name={`faixas.${indice}.temCodigo`}
          render={({ field }) => (
            <Switch
              id={idTemCodigo}
              checked={field.value}
              onCheckedChange={field.onChange}
              disabled={carregando}
            />
          )}
        />
      </div>

      <Controller
        control={control}
        name={`faixas.${indice}.temCodigo`}
        render={({ field: { value: temCodigo } }) =>
          temCodigo ? (
            <Campo id={idCodigo} rotulo="Código" erro={erros?.codigo?.message} obrigatorio>
              <Input
                id={idCodigo}
                className={CLASSE_CONTROLE}
                disabled={carregando}
                {...register(`faixas.${indice}.codigo`)}
              />
            </Campo>
          ) : (
            <Campo
              id={idLink}
              rotulo="Link de redirecionamento"
              erro={erros?.linkRedirecionamento?.message}
              obrigatorio
            >
              <Input
                id={idLink}
                type="url"
                placeholder="https://..."
                className={CLASSE_CONTROLE}
                disabled={carregando}
                {...register(`faixas.${indice}.linkRedirecionamento`)}
              />
            </Campo>
          )
        }
      />
    </div>
  );
}
